import {
  Button,
  CircularProgress,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TextField,
  Typography,
} from "@suraasa/placebo-ui"
import { useInfiniteQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { APIError } from "api/utils"
import PageContainer from "components/PageContainer"
import MasterclassCard from "features/Masterclasses/MasterclassCard"
import { BookStack, NavArrowDown, Search } from "iconoir-react"
import debounce from "lodash/debounce"
import { ChangeEvent, useCallback, useState } from "react"
import usePaginationScroll from "utils/hooks/usePaginationScroll"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

const ROWS_PER_PAGE = 12

const ExploreMasterclasses = () => {
  const [orderBy, setOrderBy] = useState({
    label: "Latest First",
    value: "-start_time",
  })

  const [searchText, setSearchText] = useState("")

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceUserText = useCallback(
    debounce((search: string) => {
      setSearchText(search)
      trackingService.trackEvent(
        TrackingEvents.masterclassSearched,
        {
          search_query: search,
        },
        { services: ["PostHog"] }
      )
    }, 1000),
    []
  )

  const params = {
    size: ROWS_PER_PAGE,
    search: searchText,
    ordering: orderBy.value,
  }

  const { isFetching, hasNextPage, fetchNextPage, data, isLoading } =
    useInfiniteQuery({
      queryKey: queries.masterclasses.masterclassesList(params).queryKey,
      queryFn: ({ pageParam = 1 }) =>
        api.masterclasses.list({
          params: {
            ...params,
            page: pageParam,
          },
        }),
      getNextPageParam: lastPage => lastPage.nextPage ?? undefined,
      onError: err => {
        if (err instanceof APIError) {
          console.error(err.statusCode, err.is500, err.__errors)
        }
      },
    })

  const { ref } = usePaginationScroll({
    loading: isFetching,
    hasNextPage,
    actionFunc: fetchNextPage,
  })

  const masterclasses = data?.pages.flatMap(page => page.data) ?? []

  return (
    <div className="mb-9 mt-1 sm:mb-5">
      <PageContainer>
        <div className="mt-3 flex flex-col items-start justify-between gap-y-1 sm:flex-row sm:items-center">
          <TextField
            placeholder="Search Masterclasses"
            startIcon={<Search className="!size-2.5 text-onSurface-600" />}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              debounceUserText(e.target.value)
            }
            className="w-full sm:w-1/2 [&>div]:rounded-xl [&>div]:border-surface-300 [&>div]:pr-1"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                color="black"
                variant="text"
                className="!px-2 !py-1.5"
                endAdornment={
                  <NavArrowDown className="!size-2.5" strokeWidth={2} />
                }
              >
                By {orderBy.label}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={() => {
                    setOrderBy({
                      label: "Latest First",
                      value: "-start_time",
                    })
                    trackingService.trackEvent(
                      TrackingEvents.masterclassSortChanged,
                      {
                        sort_type: "latest_first",
                      },
                      { services: ["PostHog"] }
                    )
                  }}
                >
                  Latest First
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setOrderBy({
                      label: "Oldest First",
                      value: "start_time",
                    })
                    trackingService.trackEvent(
                      TrackingEvents.masterclassSortChanged,
                      {
                        sort_type: "oldest_first",
                      },
                      { services: ["PostHog"] }
                    )
                  }}
                >
                  Oldest First
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-2 grid grid-cols-12 gap-3 sm:mt-4">
          {isLoading &&
            Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
              >
                <div className="overflow-hidden rounded-2xl border border-surface-200 bg-white shadow-sm">
                  <div className="h-20 w-full animate-pulse bg-surface-200" />

                  <div className="p-3">
                    <div className="mb-2 h-2 w-3/4 animate-pulse rounded bg-surface-200" />
                    <div className="h-1.75 w-1/3 animate-pulse rounded bg-surface-200" />
                  </div>
                </div>
              </div>
            ))}

          {!isLoading &&
            masterclasses.length > 0 &&
            masterclasses.map((classDetails, index) => (
              <div
                key={index}
                className="col-span-12 sm:col-span-6 md:col-span-4 lg:col-span-3"
              >
                <MasterclassCard masterclass={classDetails} />
              </div>
            ))}

          {!isLoading && hasNextPage && <div ref={ref} className="h-4" />}
        </div>

        {!isLoading && masterclasses.length === 0 && (
          <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2">
            <BookStack
              className="size-8 text-onSurface-400"
              strokeWidth={1.5}
            />
            <Typography className="text-onSurface-500">
              No masterclasses available
            </Typography>
          </div>
        )}

        {isFetching && !isLoading && (
          <div className="mt-2 flex items-center justify-center">
            <CircularProgress color="black" size="md" />
          </div>
        )}
      </PageContainer>
    </div>
  )
}

export default ExploreMasterclasses
